// @ts-nocheck
import React from 'react';
import { useColorModeValue } from 'utils/hooks';
import { Center } from 'components/base/General';
import {
  ScrollView,
  Stack,
  SizableText,
  YStack,
  XStack,
  useTheme,
} from 'tamagui';
import { ColorButton } from 'components/utility/Buttons';
import ChildPage from 'components/main/ChildPage';
import { wrapImgCDN, truncateStr, stripHTML } from 'utils/misc';
import { BlogCardProps, BlogProps } from 'interfaces/page/BlogInterfaces';
import { usePagination } from 'utils/hooks';
import { DOTS } from 'config/constants';
import { HeaderText } from 'components/utility/General';
import loadable from '@loadable/component';
import { createParam } from 'solito';
import { Link } from 'solito/link';

const Image = loadable(() => import('components/utility/CrossPlatformImage'), {
  ssr: false,
});

const { useParam } = createParam();

const BlogCard = (props: BlogCardProps) => {
  const theme = useTheme();
  const cdnURL = wrapImgCDN(props.blog.image, 800, 1920, 0, 400);
  const blogURL = `/blog/${props.blog.lang_code}/${props.blog.slug}`;
  return (
    <YStack gap={'$md'}>
      <Link href={blogURL}>
        <Stack hoverStyle={{ opacity: 0.5 }}>
          <Image
            width={800}
            height={400}
            $sm={{ width: 800, height: 400 }}
            resizeMode="cover"
            source={{
              uri: cdnURL,
            }}
          />
        </Stack>
      </Link>
      <Stack mx={8} $lg={{ mx: 0 }} gap={10}>
        <Link href={blogURL}>
          <HeaderText
            headerLevel={2}
            fontSize="$lg"
            $sm={{ fontSize: '$xl' }}
            $md={{ fontSize: '$2xl' }}
            $lg={{ fontSize: '$3xl' }}
            color="black"
            hoverStyle={{ opacity: 0.5 }}
          >
            {props.blog.title}
          </HeaderText>
        </Link>
        <SizableText fontSize="$xs" color="$gray400">
          {props.blog.date}
        </SizableText>
        <SizableText
          fontSize="$sm"
          $sm={{ fontSize: '$md' }}
          $md={{ fontSize: '$lg' }}
          $lg={{ fontSize: '$xl' }}
          isTruncated
          color={theme.borderGray}
          numberOfLines={2}
          ellipsizeMode="tail"
        >
          {truncateStr(stripHTML(props.blog.body), 300, true)}
        </SizableText>
        <Link href={blogURL}>
          <ColorButton
            title="Read More"
            width={200}
            mt={15}
            accessibilityLabel="Read More"
          />
        </Link>
      </Stack>
    </YStack>
  );
};

const TotalPages = (props: any) => {
  const { langCode, totalPages } = props;
  const theme = useTheme();
  const [page] = useParam('page');
  const currentPage = parseInt(page || '1');
  const paginationRange = usePagination(currentPage, totalPages, 1, 1);

  if (currentPage === 0 || paginationRange!.length < 2) {
    return null;
  }

  let lastPage = paginationRange![paginationRange!.length - 1];

  return (
    <XStack gap={'$xl'}>
      {currentPage > 1 && (
        <Link href={`/blog/${langCode}?page=${currentPage - 1}`}>
          <SizableText
            fontSize="$xl"
            color="black"
            hoverStyle={{ opacity: 0.5 }}
          >
            Previous
          </SizableText>
        </Link>
      )}

      {paginationRange!.map((pageNumber: number | string, index: number) => {
        if (pageNumber === DOTS) {
          return (
            <SizableText key={index} fontSize="$xl">
              {DOTS}
            </SizableText>
          );
        }

        return (
          <Link key={index} href={`/blog/${langCode}?page=${pageNumber}`}>
            <SizableText
              fontSize="xl"
              color={currentPage === pageNumber ? '$red500' : 'black'}
              hoverStyle={{ opacity: 0.5 }}
            >
              {pageNumber}
            </SizableText>
          </Link>
        );
      })}

      {currentPage < lastPage && (
        <Link href={`/blog/${langCode}?page=${currentPage + 1}`}>
          <SizableText
            fontSize="$xl"
            color="black"
            hoverStyle={{ opacity: 0.5 }}
          >
            Next
          </SizableText>
        </Link>
      )}
    </XStack>
  );
};

const Blog = (props: BlogProps) => {
  const { blogs, totalPages, langCode } = props;

  return (
    <ChildPage>
      <ScrollView
        width={900}
        alignSelf="center"
        pt={25}
        pb={50}
        flex={1}
        height="100%"
        maxWidth="100%"
      >
        <YStack gap={'$8xl'}>
          {blogs.map((blog, index) => (
            <BlogCard key={index} blog={blog} {...props} />
          ))}
        </YStack>
      </ScrollView>
      <Center py={25}>
        <TotalPages totalPages={totalPages} langCode={langCode} />
      </Center>
    </ChildPage>
  );
};

export default Blog;
